// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { Injectable, TemplateRef } from '@angular/core';

export interface ToastLink {
  text: string;
  url: string;
}

export interface ToastInfo {
  id: number;
  textOrTpl: string | TemplateRef<any>;
  classname?: string;
  delay?: number;
  header?: string;
  autohide?: boolean;
  type?: 'success' | 'error' | 'warning' | 'info';
  link?: ToastLink;
}

export interface ToastOptions {
  classname?: string;
  delay?: number;
  header?: string;
  autohide?: boolean;
  type?: 'success' | 'error' | 'warning' | 'info';
  link?: ToastLink;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  toasts: ToastInfo[] = [];
  private toastIdCounter = 0;

  /**
   * Show a toast notification
   * @param textOrTpl Message text or Angular template
   * @param options Toast configuration options
   * @returns The created toast object
   */
  show(textOrTpl: string | TemplateRef<any>, options: ToastOptions = {}): ToastInfo {
    const toast: ToastInfo = {
      id: ++this.toastIdCounter,
      textOrTpl,
      autohide: true,
      delay: 5000,
      type: 'info', // Default to info type so icon always shows
      ...options
    };

    this.toasts.push(toast);
    return toast;
  }

  /**
   * Show success toast with green styling
   */
  showSuccess(message: string, options: ToastOptions = {}): ToastInfo {
    return this.show(message, {
      classname: 'lf-toast--success',
      type: 'success',
      header: options.header || 'Success',
      ...options
    });
  }

  /**
   * Show error toast with red styling
   */
  showError(message: string, options: ToastOptions = {}): ToastInfo {
    return this.show(message, {
      classname: 'lf-toast--error',
      type: 'error',
      header: options.header || 'Error',
      delay: 8000, // Longer delay for errors
      ...options
    });
  }

  /**
   * Show warning toast with yellow styling
   */
  showWarning(message: string, options: ToastOptions = {}): ToastInfo {
    return this.show(message, {
      classname: 'lf-toast--warning',
      type: 'warning',
      header: options.header || 'Warning',
      delay: 6000,
      ...options
    });
  }

  /**
   * Show info toast with blue styling
   */
  showInfo(message: string, options: ToastOptions = {}): ToastInfo {
    return this.show(message, {
      classname: 'lf-toast--info',
      type: 'info',
      header: options.header || 'Information',
      ...options
    });
  }

  /**
   * Remove a specific toast by object reference
   */
  remove(toast: ToastInfo): void {
    this.toasts = this.toasts.filter(t => t.id !== toast.id);
  }

  /**
   * Remove a toast by ID
   */
  removeById(id: number): void {
    this.toasts = this.toasts.filter(t => t.id !== id);
  }

  /**
   * Clear all toasts
   */
  clear(): void {
    this.toasts.splice(0, this.toasts.length);
  }

  /**
   * Get number of active toasts
   */
  getCount(): number {
    return this.toasts.length;
  }
}
